import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
  Divider,
  Box,
  Chip,
  IconButton,
  makeStyles,
  useTheme
} from '@material-ui/core';
import {
  Close as CloseIcon,
  PinDrop as PinIcon,
  VolumeOff as MuteIcon,
  Archive as ArchiveIcon,
  Person as PersonIcon
} from '@material-ui/icons';

import { i18n } from '../../translate/i18n';
import * as chatService from '../../services/chatService';
import toastError from '../../errors/toastError';
import { AuthContext } from '../../context/Auth/AuthContext';
import api from '../../services/api';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: 480,
    maxWidth: 600,
    maxHeight: '80vh',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 24px',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  chatInfo: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 16,
  },
  chatAvatar: {
    width: 64,
    height: 64,
    fontSize: 24,
    fontWeight: 600,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    marginRight: 16,
  },
  chatDetails: {
    flex: 1,
  },
  chatTitle: {
    fontWeight: 600,
    fontSize: 18,
    marginBottom: 4,
  },
  participantCount: {
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: 12,
    color: theme.palette.text.primary,
  },
  settingItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 0',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  settingInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  settingIcon: {
    color: theme.palette.text.secondary,
  },
  userAvatar: {
    width: 40,
    height: 40,
    fontSize: 14,
  },
  onlineIndicator: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#4CAF50',
    border: `2px solid ${theme.palette.background.paper}`,
    position: 'absolute',
    bottom: 2,
    right: 2,
  },
  userAvatarContainer: {
    position: 'relative',
  },
  statusChip: {
    height: 20,
    fontSize: 11,
  },
}));

const ChatInfoModal = ({ open, onClose, chat, onChatUpdate }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { user, socket } = useContext(AuthContext);
  const companyId = user?.companyId;

  const [settings, setSettings] = useState({
    isPinned: false,
    isMuted: false,
    isArchived: false,
  });
  const [loading, setLoading] = useState({
    pin: false,
    mute: false,
    archive: false,
  });
  const [chatData, setChatData] = useState(chat);

  useEffect(() => {
    if (chat && open) {
      setChatData(chat);
      setSettings({
        isPinned: chat.isPinned || false,
        isMuted: chat.isMuted || false,
        isArchived: chat.isArchived || false,
      });
    }
  }, [chat, open]);

  // Atualização em tempo real dos status dos usuários (igual MomentsUser)
  useEffect(() => {
    if (!open || !socket || !companyId) return;
    let isMounted = true;
    const fetchUserStatus = async () => {
      if (!isMounted) return;
      try {
        const { data } = await api.get("/users");
        const usersData = Array.isArray(data) ? data : (data.users || []);
        if (chatData && chatData.users) {
          setChatData(prevChat => ({
            ...prevChat,
            users: prevChat.users.map(chatUser => {
              const updatedUser = usersData.find(u => u.id === chatUser.user?.id);
              return updatedUser 
                ? { ...chatUser, user: { ...chatUser.user, online: updatedUser.online } }
                : chatUser;
            })
          }));
        }
      } catch (err) {
        // Silencia erro
      }
    };
    fetchUserStatus();
    const interval = setInterval(fetchUserStatus, 6000);
    const onUserUpdate = (data) => {
      if (data.action === "update" && data.user) {
        setChatData(prevChat => {
          if (!prevChat || !prevChat.users) return prevChat;
          return {
            ...prevChat,
            users: prevChat.users.map(chatUser => 
              chatUser.user?.id === data.user.id 
                ? { ...chatUser, user: { ...chatUser.user, online: data.user.online } }
                : chatUser
            )
          };
        });
      }
    };
    socket.on(`company-${companyId}-user`, onUserUpdate);
    return () => {
      isMounted = false;
      clearInterval(interval);
      socket.off(`company-${companyId}-user`, onUserUpdate);
    };
  }, [open, socket, companyId, chatData]);

  const handleSettingChange = async (setting, value) => {
    setLoading(prev => ({ ...prev, [setting]: true }));
    
    try {
      switch (setting) {
        case 'pin':
          await chatService.pinChat(chat.id, value);
          setSettings(prev => ({ ...prev, isPinned: value }));
          break;
        case 'mute':
          await chatService.muteChat(chat.id, value);
          setSettings(prev => ({ ...prev, isMuted: value }));
          break;
        case 'archive':
          await chatService.archiveChat(chat.id, value);
          setSettings(prev => ({ ...prev, isArchived: value }));
          break;
      }
      
      // Notificar componente pai sobre a atualização
      if (onChatUpdate) {
        onChatUpdate({
          ...chat,
          isPinned: setting === 'pin' ? value : settings.isPinned,
          isMuted: setting === 'mute' ? value : settings.isMuted,
          isArchived: setting === 'archive' ? value : settings.isArchived,
        });
      }
    } catch (err) {
      console.error(`Erro ao ${setting} chat:`, err);
      toastError(err);
    } finally {
      setLoading(prev => ({ ...prev, [setting]: false }));
    }
  };

  const getAvatarText = (title) => {
    if (!title) return '?';
    const words = title.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return title.slice(0, 2).toUpperCase();
  };

  const getUserAvatarText = (name) => {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
  };
  if (!chatData) return null;
  const totalUsers = chatData.users?.length || 0;
  const onlineUsers = chatData.users?.filter(u => u.isOnline)?.length || 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: classes.dialogPaper
      }}
    >      <div className={classes.header}>        <Typography variant="h6">
          Informações do Chat
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent>
        {/* Informações do Chat */}
        <div className={classes.chatInfo}>
          <Avatar className={classes.chatAvatar}>
            {getAvatarText(chat.title)}
          </Avatar>
          <div className={classes.chatDetails}>
            <Typography className={classes.chatTitle}>
              {chat.title || 'Chat sem título'}
            </Typography>
            <Typography className={classes.participantCount}>
              {totalUsers} participante{totalUsers !== 1 ? 's' : ''}
              {onlineUsers > 0 && (
                <Chip 
                  label={`${onlineUsers} online`}
                  size="small" 
                  className={classes.statusChip}
                  style={{ marginLeft: 8, backgroundColor: '#E8F5E8', color: '#2E7D32' }}
                />
              )}
            </Typography>
          </div>
        </div>

        <Divider style={{ margin: '16px 0' }} />        {/* Configurações do Chat */}
        <div className={classes.section}>          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Configurações
          </Typography>

          <div className={classes.settingItem}>
            <div className={classes.settingInfo}>
              <PinIcon className={classes.settingIcon} />
              <div>                <Typography variant="body2">
                  Fixar Chat
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Manter este chat no topo da lista
                </Typography>
              </div>
            </div>
            <Switch
              checked={settings.isPinned}
              onChange={(e) => handleSettingChange('pin', e.target.checked)}
              disabled={loading.pin}
              color="primary"
            />
          </div>

          <div className={classes.settingItem}>
            <div className={classes.settingInfo}>
              <MuteIcon className={classes.settingIcon} />
              <div>                <Typography variant="body2">
                  Silenciar Chat
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Não receber notificações deste chat
                </Typography>
              </div>
            </div>
            <Switch
              checked={settings.isMuted}
              onChange={(e) => handleSettingChange('mute', e.target.checked)}
              disabled={loading.mute}
              color="primary"
            />
          </div>

          <div className={classes.settingItem}>
            <div className={classes.settingInfo}>
              <ArchiveIcon className={classes.settingIcon} />
              <div>                <Typography variant="body2">
                  Arquivar Chat
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Mover chat para arquivos
                </Typography>
              </div>
            </div>
            <Switch
              checked={settings.isArchived}
              onChange={(e) => handleSettingChange('archive', e.target.checked)}
              disabled={loading.archive}
              color="primary"
            />
          </div>
        </div>

        <Divider style={{ margin: '16px 0' }} />        {/* Lista de Participantes */}
        <div className={classes.section}>          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Participantes ({totalUsers})
          </Typography>

          <List dense>
            {chatData.users?.map((chatUser) => (
              <ListItem key={chatUser.user.id}>
                <ListItemAvatar>
                  <div className={classes.userAvatarContainer}>
                    <Avatar className={classes.userAvatar}>
                      {getUserAvatarText(chatUser.user.name)}
                    </Avatar>
                    {chatUser.user?.online && <div className={classes.onlineIndicator} />}
                  </div>
                </ListItemAvatar>
                <ListItemText
                  primary={chatUser.user.name}
                  secondary={chatUser.user?.online ? 'Online' : 'Offline'}
                />
                <ListItemSecondaryAction>
                  <Chip 
                    size="small"
                    label={chatUser.user?.online ? 'Online' : 'Offline'}
                    color={chatUser.user?.online ? 'primary' : 'default'}
                    variant={chatUser.user?.online ? 'default' : 'outlined'}
                    className={classes.statusChip}
                    style={{
                      backgroundColor: chatUser.user?.online ? '#25D366' : undefined,
                      color: chatUser.user?.online ? 'white' : undefined
                    }}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </div>
      </DialogContent>      <DialogActions>        <Button onClick={onClose} color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChatInfoModal;
